import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaTimes, FaTicketAlt, FaComments, FaPaperPlane, FaCalendarAlt, FaUser, FaExclamationTriangle } from 'react-icons/fa';

const TicketDetailsModal = ({ ticket, onClose, userInfo, API_BASE_URL, auth, onUpdate }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);

  const statusColors = {
    open: '#3498db',
    in_progress: '#f39c12',
    resolved: '#27ae60',
    closed: '#95a5a6',
    cancelled: '#e74c3c'
  };

  const priorityColors = {
    low: '#95a5a6',
    medium: '#3498db',
    high: '#f39c12',
    urgent: '#e74c3c'
  };

  useEffect(() => {
    if (ticket) {
      fetchComments();
    }
  }, [ticket]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/support-tickets/${ticket.ticket_id}/comments?include_internal=false`,
        auth
      );
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/support-tickets/${ticket.ticket_id}/comments`,
        {
          user_id: userInfo.user_id,
          comment: newComment,
          is_internal: false
        },
        auth
      );

      setNewComment('');
      fetchComments();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    return (
      <span style={{
        backgroundColor: statusColors[status] || '#95a5a6',
        color: 'white',
        padding: '0.25rem 0.75rem',
        borderRadius: '1rem',
        fontSize: '0.8rem',
        fontWeight: 'bold',
        textTransform: 'capitalize'
      }}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    return (
      <span style={{
        backgroundColor: priorityColors[priority] || '#95a5a6',
        color: 'white',
        padding: '0.25rem 0.75rem',
        borderRadius: '1rem',
        fontSize: '0.8rem',
        fontWeight: 'bold',
        textTransform: 'capitalize'
      }}>
        {priority}
      </span>
    );
  };

  if (!ticket) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '0',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
          color: 'white',
          padding: '2rem',
          position: 'relative'
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2rem'
            }}
          >
            <FaTimes />
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <FaTicketAlt size={24} />
            <div>
              <h2 style={{ margin: 0, fontSize: '1.5rem' }}>
                Ticket #{ticket.ticket_number}
              </h2>
              <p style={{ margin: 0, opacity: 0.9, fontSize: '1rem' }}>
                {ticket.title}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {getStatusBadge(ticket.status)}
            {getPriorityBadge(ticket.priority)}
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '2rem', maxHeight: '60vh', overflow: 'auto' }}>
          {/* Info Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '1.5rem',
              borderRadius: '12px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <FaCalendarAlt color="#6c757d" />
                <strong style={{ color: '#495057' }}>Created</strong>
              </div>
              <p style={{ margin: 0, color: '#6c757d' }}>{formatDate(ticket.created_at)}</p>
            </div>

            <div style={{
              backgroundColor: '#f8f9fa',
              padding: '1.5rem',
              borderRadius: '12px',
              border: '1px solid #e9ecef'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <FaUser color="#6c757d" />
                <strong style={{ color: '#495057' }}>Category</strong>
              </div>
              <p style={{ margin: 0, color: '#6c757d', textTransform: 'capitalize' }}>
                {ticket.category.replace('_', ' ')}
              </p>
            </div>
          </div>

          {/* Description */}
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '1px solid #e9ecef',
            marginBottom: '2rem'
          }}>
            <h4 style={{ margin: '0 0 1rem 0', color: '#495057' }}>Description</h4>
            <p style={{ margin: 0, color: '#6c757d', lineHeight: '1.6' }}>
              {ticket.description}
            </p>
          </div>

          {/* Comments Section */}
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '1.5rem',
            borderRadius: '12px',
            border: '1px solid #e9ecef'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <FaComments color="#3498db" />
              <h4 style={{ margin: 0, color: '#495057' }}>Comments ({comments.length})</h4>
            </div>

            {/* Comments List */}
            <div style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid #dee2e6',
              borderRadius: '0.25rem',
              marginBottom: '1rem'
            }}>
              {comments.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#7f8c8d' }}>
                  No comments yet
                </div>
              ) : (
                comments.map((comment) => (
                  <div
                    key={comment.comment_id}
                    style={{
                      padding: '1rem',
                      borderBottom: '1px solid #dee2e6',
                      backgroundColor: 'white'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <FaUser size={12} color="#7f8c8d" />
                        <strong style={{ color: '#495057', fontSize: '0.9rem' }}>
                          {comment.user_name}
                        </strong>
                        <span style={{
                          backgroundColor: comment.role_name === 'field_officer' ? '#3498db' : '#27ae60',
                          color: 'white',
                          padding: '0.1rem 0.5rem',
                          borderRadius: '0.75rem',
                          fontSize: '0.7rem',
                          textTransform: 'capitalize'
                        }}>
                          {comment.role_name?.replace('_', ' ')}
                        </span>
                      </div>
                      <span style={{ color: '#7f8c8d', fontSize: '0.8rem' }}>
                        {formatDate(comment.created_at)}
                      </span>
                    </div>
                    <p style={{ margin: 0, color: '#495057', lineHeight: '1.5' }}>
                      {comment.comment}
                    </p>
                  </div>
                ))
              )}
            </div>

            {/* Add Comment Form */}
            <form onSubmit={handleAddComment}>
              <div style={{ marginBottom: '1rem' }}>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '0.75rem',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.25rem',
                    fontSize: '0.9rem',
                    resize: 'vertical',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={loading || !newComment.trim()}
                style={{
                  backgroundColor: loading || !newComment.trim() ? '#95a5a6' : '#3498db',
                  color: 'white',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.25rem',
                  cursor: loading || !newComment.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaPaperPlane />
                {loading ? 'Posting...' : 'Post Comment'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetailsModal;
